import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
// Iter 142.6 — Sentry must initialize before React renders so we capture
// any boot-time throws + so route-change tracing is wired before the
// first navigation event fires.
import { initSentry, SentryErrorBoundary } from "@/lib/sentry";
initSentry();

function AppReadySignal() {
  React.useEffect(() => {
    const fireReady = () => window.dispatchEvent(new Event("vl:app-ready"));
    if ("requestAnimationFrame" in window) {
      requestAnimationFrame(() => requestAnimationFrame(fireReady));
    } else {
      setTimeout(fireReady, 0);
    }
  }, []);
  return null;
}

// Register the service worker as early as possible so the browser can flag
// the site as installable (a prerequisite for `beforeinstallprompt`).
// `/sw.js` already handles push + notifications; same file powers PWA install.
// Keep a versioned URL so stale CDN-cached service workers cannot hold the app
// on an old shell after a production deploy.
const SERVICE_WORKER_URL = "/sw.js?v=20260628-landing-shell";

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(SERVICE_WORKER_URL)
      .then((registration) => {
        registration.update?.();
        if (registration.waiting) {
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
        }
      })
      .catch((err) => console.warn("SW registration failed:", err));
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));

async function recoverFromRenderError(resetError) {
  try {
    resetError?.();
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => {
        registration.active?.postMessage?.({ type: "CLEAR_REELISLE_CACHES" });
        registration.waiting?.postMessage?.({ type: "SKIP_WAITING" });
        return registration.unregister?.().catch(() => {});
      }));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.filter((key) => key.startsWith("ri-")).map((key) => caches.delete(key)));
    }
  } catch {}
  window.location.reload();
}

root.render(
  <React.StrictMode>
    <SentryErrorBoundary
      fallback={({ error, resetError }) => (
        <div style={{
          minHeight: "100vh",
          background: "#0f0f1f",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}>
          <div style={{ maxWidth: 480, textAlign: "center" }}>
            <h1 style={{ fontSize: 28, marginBottom: 12 }}>Something broke</h1>
            <p style={{ opacity: 0.75, marginBottom: 20, fontSize: 15 }}>
              We've been notified — please try refreshing.
            </p>
            <button
              onClick={() => recoverFromRenderError(resetError)}
              style={{
                background: "linear-gradient(135deg, #D4AF37, #F5D76E)",
                color: "#000",
                border: "none",
                padding: "12px 28px",
                borderRadius: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
              data-testid="sentry-error-boundary-retry"
            >
              Refresh app
            </button>
            {process.env.NODE_ENV !== "production" && error?.message && (
              <pre style={{
                marginTop: 24, opacity: 0.5, fontSize: 11,
                whiteSpace: "pre-wrap", textAlign: "left",
              }}>
                {String(error.message).slice(0, 400)}
              </pre>
            )}
          </div>
        </div>
      )}
    >
      <AppReadySignal />
      <App />
    </SentryErrorBoundary>
  </React.StrictMode>,
);
